/**
 * Typed API client (U6) for the /api/v1 surface in docs/ARCHITECTURE.md.
 *
 * Every call = fetch + zod parse against @buildledger/shared schemas — DTO
 * shapes are NEVER redefined here; list envelopes compose shared schemas
 * (z.array / paginated). Feature units consume these; where ARCHITECTURE.md
 * leaves a response shape implicit this file picks the conventional one
 * (create/update → entity, delete → OkResponse, list → array or paginated)
 * and the owning API unit should match it. Anything a feature needs beyond
 * this map can be built in the feature dir from `request()` in ./http.
 */
import { z } from 'zod';
import {
  AssemblySchema,
  AuditEventsResponseSchema,
  ClientContactSchema,
  ClientDetailResponseSchema,
  ClientEstimatesResponseSchema,
  ClientSchema,
  CommitImportResponseSchema,
  CompanySchema,
  CompanyTradesResponseSchema,
  CompanyUserSchema,
  CompliancePacksResponseSchema,
  ComplianceRulesResponseSchema,
  CopyFromEstimateResponseSchema,
  CrewSchema,
  DashboardAlertsResponseSchema,
  DashboardMetricsResponseSchema,
  DashboardPipelineResponseSchema,
  DuplicateEstimateResponseSchema,
  EquipmentRateSchema,
  EstimateDetailResponseSchema,
  EstimateNoteSchema,
  EstimateSectionSchema,
  EstimateSummarySchema,
  EstimateTemplateSchema,
  EstimateVersionDetailSchema,
  EstimateVersionsResponseSchema,
  ExportJobSchema,
  FileSchema,
  InsuranceRecordSchema,
  LaborRoleSchema,
  LibraryImportSchema,
  LicenseRecordSchema,
  LinesBatchResponseSchema,
  MaterialSchema,
  MaterialComparisonSchema,
  MaterialListDetailSchema,
  MaterialListItemSchema,
  MaterialListSchema,
  MaterialQuoteSchema,
  MaterialVendorSchema,
  HelpArticleSchema,
  HelpIndexResponseSchema,
  TonyReplySchema,
  TonySuggestionsResponseSchema,
  SubMaterialListDetailSchema,
  SubMaterialListSummarySchema,
  OkResponseSchema,
  OutboxEmailSchema,
  OutboxListResponseSchema,
  ProposalPreviewResponseSchema,
  ProposalSchema,
  ReviseEstimateResponseSchema,
  ScopePackageDetailResponseSchema,
  ScopePackageGrantSchema,
  ScopePackageQuotesResponseSchema,
  ScopePackageSchema,
  AcceptSubQuoteResponseSchema,
  SendEstimateResponseSchema,
  SubInboxResponseSchema,
  SubQuoteSchema,
  TradeProfileResponseSchema,
  TradeProfilesListResponseSchema,
  VettingStateResponseSchema,
  VettingSubmissionSchema,
  WageRateSetSchema,
  paginated,
  AuditQuerySchema,
  ComplianceRulesQuerySchema,
  EstimateListQuerySchema,
  PaginationQuerySchema,
  type CreateAssemblyRequest,
  type CreateClientActivityRequest,
  type CreateClientContactRequest,
  type CreateClientRequest,
  type CreateCrewRequest,
  type CreateEquipmentRateRequest,
  type CreateEstimateRequest,
  type CreateEstimateTemplateRequest,
  type CreateInsuranceRecordRequest,
  type CreateInviteRequest,
  type CreateLaborRoleRequest,
  type CreateLicenseRecordRequest,
  type CreateMaterialRequest,
  type CreateMaterialItemsRequest,
  type CreateMaterialListRequest,
  type CreateSubMaterialItemsRequest,
  type AskTonyRequest,
  type ImportMaterialListRequest,
  type PriceMaterialListRequest,
  type RecordMaterialQuoteRequest,
  type UpdateMaterialItemRequest,
  type UpdateMaterialListRequest,
  type UpdateSubMaterialItemRequest,
  type CreateProjectSiteRequest,
  type CreateScopePackageRequest,
  type CreateVettingRequest,
  type CreateWageRateSetRequest,
  type CopyFromEstimateRequest,
  type CreateCompanyUserRequest,
  type DeclineSubQuoteRequest,
  type LinesBatchRequest,
  type PutSubQuoteRequest,
  type SendEstimateRequest,
  type UpdateAssemblyRequest,
  type UpdateCapabilitiesRequest,
  type UpdateClientContactRequest,
  type UpdateClientRequest,
  type UpdateCompanyRequest,
  type UpdateCompanyUserRequest,
  type UpdateCrewRequest,
  type UpdateEquipmentRateRequest,
  type UpdateEstimateRequest,
  type UpdateEstimateTemplateRequest,
  type UpdateImportMappingRequest,
  type UpdateInsuranceRecordRequest,
  type UpdateLaborRoleRequest,
  type UpdateLicenseRecordRequest,
  type UpdateMaterialRequest,
  type UpdateProjectSiteRequest,
  type UpdateProposalRequest,
  type UpdateScopePackageRequest,
  type UpdateSectionRequest,
  type UpdateWageRateSetRequest,
  type CreateNoteRequest,
  type CreateSectionRequest,
  type ReorderSectionsRequest,
  type UpdateNoteRequest,
  ClientActivitySchema,
  ProjectSiteSchema,
  type AcceptPortionRequest,
  type BulkCreateProjectWorkItemsRequest,
  type CompleteSubPortionRequest,
  type CreateSubWorkLogRequest,
  type CreateProjectAssignmentRequest,
  type CreateProjectFromEstimateRequest,
  type CreateProjectRequest,
  type CreateProjectWorkItemRequest,
  type CreateProjectWorkLogRequest,
  ProjectAssignmentSchema,
  ProjectDetailResponseSchema,
  ProjectProgressResponseSchema,
  ProjectSchema,
  ProjectSubCandidateSchema,
  ProjectSummarySchema,
  ProjectWorkItemSchema,
  ProjectWorkLogSchema,
  type RemoveAssignmentRequest,
  type ReopenPortionRequest,
  type ReopenSubPortionRequest,
  type SetWorkItemStatusRequest,
  SubProjectDetailSchema,
  SubProjectSummarySchema,
  type UpdateProjectAssignmentRequest,
  type UpdateProjectRequest,
  type UpdateProjectWorkItemRequest,
  type UpdateSubWorkItemRequest,
} from '@buildledger/shared';
import { API_BASE, request, type QueryValue } from './http';

type PaginationInput = z.input<typeof PaginationQuerySchema>;

/** Narrow a typed query object to the primitive record buildQuery accepts. */
function q(params: object | undefined): Record<string, QueryValue> | undefined {
  return params as Record<string, QueryValue> | undefined;
}

// ------------------------------------------------------------------ company

export const companyApi = {
  get: () => request({ path: '/company', schema: CompanySchema }),
  update: (body: UpdateCompanyRequest) =>
    request({ method: 'PATCH', path: '/company', body, schema: CompanySchema }),
  updateCapabilities: (body: UpdateCapabilitiesRequest) =>
    request({ method: 'PATCH', path: '/company/capabilities', body, schema: CompanySchema }),

  trades: () => request({ path: '/company/trades', schema: CompanyTradesResponseSchema }),
  addTrade: (tradeProfileKey: string) =>
    request({
      method: 'POST',
      path: '/company/trades',
      body: { tradeProfileKey },
      schema: CompanyTradesResponseSchema,
    }),
  removeTrade: (tradeProfileKey: string) =>
    request({
      method: 'DELETE',
      path: `/company/trades/${encodeURIComponent(tradeProfileKey)}`,
      schema: OkResponseSchema,
    }),

  licenses: () => request({ path: '/company/licenses', schema: z.array(LicenseRecordSchema) }),
  createLicense: (body: CreateLicenseRecordRequest) =>
    request({ method: 'POST', path: '/company/licenses', body, schema: LicenseRecordSchema }),
  updateLicense: (id: string, body: UpdateLicenseRecordRequest) =>
    request({
      method: 'PATCH',
      path: `/company/licenses/${id}`,
      body,
      schema: LicenseRecordSchema,
    }),
  deleteLicense: (id: string) =>
    request({ method: 'DELETE', path: `/company/licenses/${id}`, schema: OkResponseSchema }),

  insurance: () => request({ path: '/company/insurance', schema: z.array(InsuranceRecordSchema) }),
  createInsurance: (body: CreateInsuranceRecordRequest) =>
    request({ method: 'POST', path: '/company/insurance', body, schema: InsuranceRecordSchema }),
  updateInsurance: (id: string, body: UpdateInsuranceRecordRequest) =>
    request({
      method: 'PATCH',
      path: `/company/insurance/${id}`,
      body,
      schema: InsuranceRecordSchema,
    }),
  deleteInsurance: (id: string) =>
    request({ method: 'DELETE', path: `/company/insurance/${id}`, schema: OkResponseSchema }),

  users: () => request({ path: '/company/users', schema: z.array(CompanyUserSchema) }),
  createUser: (body: CreateCompanyUserRequest) =>
    request({ method: 'POST', path: '/company/users', body, schema: CompanyUserSchema }),
  updateUser: (id: string, body: UpdateCompanyUserRequest) =>
    request({ method: 'PATCH', path: `/company/users/${id}`, body, schema: CompanyUserSchema }),

  /** multipart: field `file`. Returns the updated company (branding.logoFileId set). */
  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request({
      method: 'POST',
      path: '/company/branding/logo',
      body: form,
      schema: CompanySchema,
    });
  },
};

// ------------------------------------------------------------ trade profiles

export const tradeProfilesApi = {
  list: () => request({ path: '/trade-profiles', schema: TradeProfilesListResponseSchema }),
  get: (key: string) =>
    request({
      path: `/trade-profiles/${encodeURIComponent(key)}`,
      schema: TradeProfileResponseSchema,
    }),
};

// ------------------------------------------------------------------ library

function crud<TEntity extends z.ZodTypeAny, TCreate, TUpdate>(base: string, entity: TEntity) {
  return {
    list: () => request({ path: base, schema: z.array(entity) }),
    get: (id: string) => request({ path: `${base}/${id}`, schema: entity }),
    create: (body: TCreate) => request({ method: 'POST', path: base, body, schema: entity }),
    update: (id: string, body: TUpdate) =>
      request({ method: 'PATCH', path: `${base}/${id}`, body, schema: entity }),
    remove: (id: string) =>
      request({ method: 'DELETE', path: `${base}/${id}`, schema: OkResponseSchema }),
  };
}

export const libraryApi = {
  laborRoles: crud<typeof LaborRoleSchema, CreateLaborRoleRequest, UpdateLaborRoleRequest>(
    '/library/labor-roles',
    LaborRoleSchema,
  ),
  crews: crud<typeof CrewSchema, CreateCrewRequest, UpdateCrewRequest>(
    '/library/crews',
    CrewSchema,
  ),
  materials: crud<typeof MaterialSchema, CreateMaterialRequest, UpdateMaterialRequest>(
    '/library/materials',
    MaterialSchema,
  ),
  equipment: crud<
    typeof EquipmentRateSchema,
    CreateEquipmentRateRequest,
    UpdateEquipmentRateRequest
  >('/library/equipment', EquipmentRateSchema),
  assemblies: crud<typeof AssemblySchema, CreateAssemblyRequest, UpdateAssemblyRequest>(
    '/library/assemblies',
    AssemblySchema,
  ),
  wageRateSets: crud<typeof WageRateSetSchema, CreateWageRateSetRequest, UpdateWageRateSetRequest>(
    '/library/wage-rate-sets',
    WageRateSetSchema,
  ),

  /** CSV import pipeline: upload → mapping → commit (U9 owns the screens). */
  createImport: (target: string, file: File) => {
    const form = new FormData();
    form.append('target', target);
    form.append('file', file);
    return request({
      method: 'POST',
      path: '/library/imports',
      body: form,
      schema: LibraryImportSchema,
    });
  },
  getImport: (id: string) =>
    request({ path: `/library/imports/${id}`, schema: LibraryImportSchema }),
  setImportMapping: (id: string, body: UpdateImportMappingRequest) =>
    request({
      method: 'PUT',
      path: `/library/imports/${id}/mapping`,
      body,
      schema: LibraryImportSchema,
    }),
  commitImport: (id: string) =>
    request({
      method: 'POST',
      path: `/library/imports/${id}/commit`,
      schema: CommitImportResponseSchema,
    }),

  copyFromEstimate: (body: CopyFromEstimateRequest) =>
    request({
      method: 'POST',
      path: '/library/copy-from-estimate',
      body,
      schema: CopyFromEstimateResponseSchema,
    }),
};

// ------------------------------------------------------------------- clients

export const clientsApi = {
  list: (query?: PaginationInput & { q?: string }) =>
    request({ path: '/clients', query: q(query), schema: paginated(ClientSchema) }),
  get: (id: string) => request({ path: `/clients/${id}`, schema: ClientDetailResponseSchema }),
  create: (body: CreateClientRequest) =>
    request({ method: 'POST', path: '/clients', body, schema: ClientSchema }),
  update: (id: string, body: UpdateClientRequest) =>
    request({ method: 'PATCH', path: `/clients/${id}`, body, schema: ClientSchema }),
  remove: (id: string) =>
    request({ method: 'DELETE', path: `/clients/${id}`, schema: OkResponseSchema }),

  createContact: (clientId: string, body: CreateClientContactRequest) =>
    request({
      method: 'POST',
      path: `/clients/${clientId}/contacts`,
      body,
      schema: ClientContactSchema,
    }),
  updateContact: (clientId: string, contactId: string, body: UpdateClientContactRequest) =>
    request({
      method: 'PATCH',
      path: `/clients/${clientId}/contacts/${contactId}`,
      body,
      schema: ClientContactSchema,
    }),
  removeContact: (clientId: string, contactId: string) =>
    request({
      method: 'DELETE',
      path: `/clients/${clientId}/contacts/${contactId}`,
      schema: OkResponseSchema,
    }),

  createSite: (clientId: string, body: CreateProjectSiteRequest) =>
    request({
      method: 'POST',
      path: `/clients/${clientId}/sites`,
      body,
      schema: ProjectSiteSchema,
    }),
  updateSite: (clientId: string, siteId: string, body: UpdateProjectSiteRequest) =>
    request({
      method: 'PATCH',
      path: `/clients/${clientId}/sites/${siteId}`,
      body,
      schema: ProjectSiteSchema,
    }),

  activity: (clientId: string) =>
    request({ path: `/clients/${clientId}/activity`, schema: z.array(ClientActivitySchema) }),
  addActivity: (clientId: string, body: CreateClientActivityRequest) =>
    request({
      method: 'POST',
      path: `/clients/${clientId}/activity`,
      body,
      schema: ClientActivitySchema,
    }),

  estimates: (clientId: string) =>
    request({ path: `/clients/${clientId}/estimates`, schema: ClientEstimatesResponseSchema }),
};

// ----------------------------------------------------------------- estimates

export const estimatesApi = {
  list: (query?: z.input<typeof EstimateListQuerySchema>) =>
    request({ path: '/estimates', query: q(query), schema: paginated(EstimateSummarySchema) }),
  create: (body: CreateEstimateRequest) =>
    request({ method: 'POST', path: '/estimates', body, schema: EstimateDetailResponseSchema }),
  /** Draft + server-computed totals (the engine on the server is the authority). */
  get: (id: string) => request({ path: `/estimates/${id}`, schema: EstimateDetailResponseSchema }),
  update: (id: string, body: UpdateEstimateRequest) =>
    request({
      method: 'PATCH',
      path: `/estimates/${id}`,
      body,
      schema: EstimateDetailResponseSchema,
    }),

  linesBatch: (id: string, body: LinesBatchRequest) =>
    request({
      method: 'PUT',
      path: `/estimates/${id}/lines:batch`,
      body,
      schema: LinesBatchResponseSchema,
    }),

  createSection: (id: string, body: CreateSectionRequest) =>
    request({
      method: 'POST',
      path: `/estimates/${id}/sections`,
      body,
      schema: EstimateSectionSchema,
    }),
  updateSection: (id: string, sectionId: string, body: UpdateSectionRequest) =>
    request({
      method: 'PATCH',
      path: `/estimates/${id}/sections/${sectionId}`,
      body,
      schema: EstimateSectionSchema,
    }),
  removeSection: (id: string, sectionId: string) =>
    request({
      method: 'DELETE',
      path: `/estimates/${id}/sections/${sectionId}`,
      schema: OkResponseSchema,
    }),
  reorderSections: (id: string, body: ReorderSectionsRequest) =>
    request({
      method: 'PUT',
      path: `/estimates/${id}/sections:reorder`,
      body,
      schema: z.array(EstimateSectionSchema),
    }),

  createNote: (id: string, body: CreateNoteRequest) =>
    request({ method: 'POST', path: `/estimates/${id}/notes`, body, schema: EstimateNoteSchema }),
  updateNote: (id: string, noteId: string, body: UpdateNoteRequest) =>
    request({
      method: 'PATCH',
      path: `/estimates/${id}/notes/${noteId}`,
      body,
      schema: EstimateNoteSchema,
    }),
  removeNote: (id: string, noteId: string) =>
    request({
      method: 'DELETE',
      path: `/estimates/${id}/notes/${noteId}`,
      schema: OkResponseSchema,
    }),

  duplicate: (id: string) =>
    request({
      method: 'POST',
      path: `/estimates/${id}/duplicate`,
      schema: DuplicateEstimateResponseSchema,
    }),
  /** The freeze transaction: version + render + token + outbox email + audit. */
  send: (id: string, body: SendEstimateRequest) =>
    request({
      method: 'POST',
      path: `/estimates/${id}/send`,
      body,
      schema: SendEstimateResponseSchema,
    }),
  revise: (id: string) =>
    request({
      method: 'POST',
      path: `/estimates/${id}/revise`,
      schema: ReviseEstimateResponseSchema,
    }),

  versions: (id: string) =>
    request({ path: `/estimates/${id}/versions`, schema: EstimateVersionsResponseSchema }),
  version: (versionId: string) =>
    request({ path: `/estimate-versions/${versionId}`, schema: EstimateVersionDetailSchema }),
  versionPdfUrl: (versionId: string) => `${API_BASE}/estimate-versions/${versionId}/pdf`,

  templates: () =>
    request({ path: '/estimate-templates', schema: z.array(EstimateTemplateSchema) }),
  createTemplate: (body: CreateEstimateTemplateRequest) =>
    request({ method: 'POST', path: '/estimate-templates', body, schema: EstimateTemplateSchema }),
  updateTemplate: (id: string, body: UpdateEstimateTemplateRequest) =>
    request({
      method: 'PATCH',
      path: `/estimate-templates/${id}`,
      body,
      schema: EstimateTemplateSchema,
    }),
  removeTemplate: (id: string) =>
    request({ method: 'DELETE', path: `/estimate-templates/${id}`, schema: OkResponseSchema }),
};

// ----------------------------------------------------------------- proposals

export const proposalsApi = {
  get: (estimateId: string) =>
    request({ path: `/estimates/${estimateId}/proposal`, schema: ProposalSchema }),
  update: (estimateId: string, body: UpdateProposalRequest) =>
    request({
      method: 'PATCH',
      path: `/estimates/${estimateId}/proposal`,
      body,
      schema: ProposalSchema,
    }),
  preview: (estimateId: string) =>
    request({
      path: `/estimates/${estimateId}/proposal/preview`,
      schema: ProposalPreviewResponseSchema,
    }),
};

// -------------------------------------------------------- seam (contractor)

export const scopePackagesApi = {
  list: (query?: PaginationInput) =>
    request({ path: '/scope-packages', query: q(query), schema: paginated(ScopePackageSchema) }),
  createForEstimate: (estimateId: string, body: CreateScopePackageRequest) =>
    request({
      method: 'POST',
      path: `/estimates/${estimateId}/scope-packages`,
      body,
      schema: ScopePackageSchema,
    }),
  get: (id: string) =>
    request({ path: `/scope-packages/${id}`, schema: ScopePackageDetailResponseSchema }),
  update: (id: string, body: UpdateScopePackageRequest) =>
    request({ method: 'PATCH', path: `/scope-packages/${id}`, body, schema: ScopePackageSchema }),
  publish: (id: string) =>
    request({ method: 'POST', path: `/scope-packages/${id}/publish`, schema: ScopePackageSchema }),

  invite: (id: string, body: CreateInviteRequest) =>
    request({
      method: 'POST',
      path: `/scope-packages/${id}/invites`,
      body,
      schema: ScopePackageGrantSchema,
    }),
  revokeInvite: (id: string, grantId: string) =>
    request({
      method: 'DELETE',
      path: `/scope-packages/${id}/invites/${grantId}`,
      schema: OkResponseSchema,
    }),

  /** Submitted+ quotes only — draft quotes are invisible to the contractor. */
  quotes: (id: string) =>
    request({ path: `/scope-packages/${id}/quotes`, schema: ScopePackageQuotesResponseSchema }),
  acceptQuote: (id: string, quoteId: string) =>
    request({
      method: 'POST',
      path: `/scope-packages/${id}/quotes/${quoteId}/accept`,
      schema: AcceptSubQuoteResponseSchema,
    }),
  declineQuote: (id: string, quoteId: string, body: DeclineSubQuoteRequest) =>
    request({
      method: 'POST',
      path: `/scope-packages/${id}/quotes/${quoteId}/decline`,
      body,
      schema: OkResponseSchema,
    }),

  requestVetting: (id: string, body: CreateVettingRequest) =>
    request({
      method: 'POST',
      path: `/scope-packages/${id}/vetting-request`,
      body,
      schema: VettingStateResponseSchema,
    }),
};

// --------------------------------------------------------------- seam (sub)

export const subApi = {
  inbox: () => request({ path: '/sub/scope-packages', schema: SubInboxResponseSchema }),
  package: (id: string) =>
    request({ path: `/sub/scope-packages/${id}`, schema: ScopePackageDetailResponseSchema }),
  quote: (packageId: string) =>
    request({ path: `/sub/scope-packages/${packageId}/quote`, schema: SubQuoteSchema }),
  saveQuote: (packageId: string, body: PutSubQuoteRequest) =>
    request({
      method: 'PUT',
      path: `/sub/scope-packages/${packageId}/quote`,
      body,
      schema: SubQuoteSchema,
    }),
  submitQuote: (packageId: string) =>
    request({
      method: 'POST',
      path: `/sub/scope-packages/${packageId}/quote/submit`,
      schema: SubQuoteSchema,
    }),
  withdrawQuote: (packageId: string) =>
    request({
      method: 'POST',
      path: `/sub/scope-packages/${packageId}/quote/withdraw`,
      schema: SubQuoteSchema,
    }),
  saveVetting: (packageId: string, body: object) =>
    request({
      method: 'PUT',
      path: `/sub/scope-packages/${packageId}/vetting`,
      body,
      schema: VettingSubmissionSchema,
    }),
};

// ---------------------------------------------------------------- compliance

export const complianceApi = {
  rules: (query?: z.input<typeof ComplianceRulesQuerySchema>) =>
    request({ path: '/compliance/rules', query: q(query), schema: ComplianceRulesResponseSchema }),
  packs: () => request({ path: '/compliance/packs', schema: CompliancePacksResponseSchema }),
};

// ----------------------------------------------------------------- dashboard

export const dashboardApi = {
  pipeline: () => request({ path: '/dashboard/pipeline', schema: DashboardPipelineResponseSchema }),
  metrics: (days?: number) =>
    request({
      path: '/dashboard/metrics',
      query: days === undefined ? undefined : { days },
      schema: DashboardMetricsResponseSchema,
    }),
  alerts: () => request({ path: '/dashboard/alerts', schema: DashboardAlertsResponseSchema }),
};

// ------------------------------------------------------- files/exports/audit

export const filesApi = {
  upload: (file: File, purpose: string) => {
    const form = new FormData();
    form.append('purpose', purpose);
    form.append('file', file);
    return request({ method: 'POST', path: '/files', body: form, schema: FileSchema });
  },
  /** Binary download/display URL (used in <img src> / anchors, not fetched). */
  url: (id: string) => `${API_BASE}/files/${id}`,
};

export const exportsApi = {
  create: () => request({ method: 'POST', path: '/exports', schema: ExportJobSchema }),
  get: (id: string) => request({ path: `/exports/${id}`, schema: ExportJobSchema }),
  downloadUrl: (id: string) => `${API_BASE}/exports/${id}/download`,
};

export const auditApi = {
  events: (query?: z.input<typeof AuditQuerySchema>) =>
    request({ path: '/audit-events', query: q(query), schema: AuditEventsResponseSchema }),
};

export const outboxApi = {
  list: () => request({ path: '/dev/outbox', schema: OutboxListResponseSchema }),
  get: (id: string) => request({ path: `/dev/outbox/${id}`, schema: OutboxEmailSchema }),
};

// ----------------------------------------------------------------- projects

/**
 * Execution tracking, contractor side. Mirrors `clientsApi`'s shape; DTOs come
 * from @buildledger/shared and are never redefined here.
 */
export const projectsApi = {
  list: (query?: PaginationInput & { q?: string; status?: string }) =>
    request({ path: '/projects', query: q(query), schema: paginated(ProjectSummarySchema) }),
  get: (id: string) => request({ path: `/projects/${id}`, schema: ProjectDetailResponseSchema }),
  create: (body: CreateProjectRequest) =>
    request({ method: 'POST', path: '/projects', body, schema: ProjectSchema }),
  update: (id: string, body: UpdateProjectRequest) =>
    request({ method: 'PATCH', path: `/projects/${id}`, body, schema: ProjectSchema }),

  progress: (id: string) =>
    request({ path: `/projects/${id}/progress`, schema: ProjectProgressResponseSchema }),
  log: (id: string, query?: { limit?: number; assignmentId?: string }) =>
    request({
      path: `/projects/${id}/log`,
      query: q(query),
      schema: z.array(ProjectWorkLogSchema),
    }),
  candidates: (id: string) =>
    request({ path: `/projects/${id}/candidates`, schema: z.array(ProjectSubCandidateSchema) }),

  addAssignment: (id: string, body: CreateProjectAssignmentRequest) =>
    request({
      method: 'POST',
      path: `/projects/${id}/assignments`,
      body,
      schema: ProjectAssignmentSchema,
    }),
  updateAssignment: (id: string, assignmentId: string, body: UpdateProjectAssignmentRequest) =>
    request({
      method: 'PATCH',
      path: `/projects/${id}/assignments/${assignmentId}`,
      body,
      schema: ProjectAssignmentSchema,
    }),
  acceptPortion: (id: string, assignmentId: string, body: AcceptPortionRequest) =>
    request({
      method: 'POST',
      path: `/projects/${id}/assignments/${assignmentId}/accept`,
      body,
      schema: ProjectAssignmentSchema,
    }),
  reopenPortion: (id: string, assignmentId: string, body: ReopenPortionRequest) =>
    request({
      method: 'POST',
      path: `/projects/${id}/assignments/${assignmentId}/reopen`,
      body,
      schema: ProjectAssignmentSchema,
    }),
  removeAssignment: (id: string, assignmentId: string, body: RemoveAssignmentRequest) =>
    request({
      method: 'POST',
      path: `/projects/${id}/assignments/${assignmentId}/remove`,
      body,
      schema: ProjectAssignmentSchema,
    }),

  addWorkItem: (id: string, assignmentId: string, body: CreateProjectWorkItemRequest) =>
    request({
      method: 'POST',
      path: `/projects/${id}/assignments/${assignmentId}/work-items`,
      body,
      schema: ProjectWorkItemSchema,
    }),
  addWorkItems: (id: string, assignmentId: string, body: BulkCreateProjectWorkItemsRequest) =>
    request({
      method: 'POST',
      path: `/projects/${id}/assignments/${assignmentId}/work-items/bulk`,
      body,
      schema: z.array(ProjectWorkItemSchema),
    }),
  addLog: (id: string, assignmentId: string, body: CreateProjectWorkLogRequest) =>
    request({
      method: 'POST',
      path: `/projects/${id}/assignments/${assignmentId}/log`,
      body,
      schema: ProjectWorkLogSchema,
    }),
  updateWorkItem: (id: string, itemId: string, body: UpdateProjectWorkItemRequest) =>
    request({
      method: 'PATCH',
      path: `/projects/${id}/work-items/${itemId}`,
      body,
      schema: ProjectWorkItemSchema,
    }),
  /** Checking a box — needs only `work-logs:write`, unlike the PATCH above. */
  setWorkItemStatus: (id: string, itemId: string, body: SetWorkItemStatusRequest) =>
    request({
      method: 'POST',
      path: `/projects/${id}/work-items/${itemId}/status`,
      body,
      schema: ProjectWorkItemSchema,
    }),
  removeWorkItem: (id: string, itemId: string) =>
    request({
      method: 'DELETE',
      path: `/projects/${id}/work-items/${itemId}`,
      schema: OkResponseSchema,
    }),

  /** The accepted-estimate → project entry point. */
  createFromEstimate: (estimateId: string, body: CreateProjectFromEstimateRequest) =>
    request({
      method: 'POST',
      path: `/estimates/${estimateId}/projects`,
      body,
      schema: ProjectSchema,
    }),
  forEstimate: (estimateId: string) =>
    request({ path: `/estimates/${estimateId}/projects`, schema: z.array(ProjectSchema) }),
};

/**
 * Execution tracking, subcontractor side. Every route is addressed by PROJECT
 * id — a sub has exactly one assignment per project, so there is no assignment
 * id in any of these URLs to guess at. Mutations return the whole detail so a
 * screen stays consistent in one round trip.
 */
export const subProjectsApi = {
  inbox: () => request({ path: '/sub/projects', schema: z.array(SubProjectSummarySchema) }),
  get: (projectId: string) =>
    request({ path: `/sub/projects/${projectId}`, schema: SubProjectDetailSchema }),
  setItemStatus: (projectId: string, itemId: string, body: UpdateSubWorkItemRequest) =>
    request({
      method: 'PATCH',
      path: `/sub/projects/${projectId}/work-items/${itemId}`,
      body,
      schema: SubProjectDetailSchema,
    }),
  addLog: (projectId: string, body: CreateSubWorkLogRequest) =>
    request({
      method: 'POST',
      path: `/sub/projects/${projectId}/log`,
      body,
      schema: SubProjectDetailSchema,
    }),
  completePortion: (projectId: string, body: CompleteSubPortionRequest) =>
    request({
      method: 'POST',
      path: `/sub/projects/${projectId}/complete`,
      body,
      schema: SubProjectDetailSchema,
    }),
  reopenPortion: (projectId: string, body: ReopenSubPortionRequest) =>
    request({
      method: 'POST',
      path: `/sub/projects/${projectId}/reopen`,
      body,
      schema: SubProjectDetailSchema,
    }),
};

/**
 * Materials, contractor side. Lists of what a job needs plus the
 * cheapest-single-store comparison. DTOs come from @buildledger/shared and are
 * never redefined here.
 */
export const materialsApi = {
  list: (query?: PaginationInput & { q?: string; status?: string }) =>
    request({
      path: '/material-lists',
      query: q(query),
      schema: paginated(MaterialListSchema),
    }),
  get: (listId: string) =>
    request({ path: `/material-lists/${listId}`, schema: MaterialListDetailSchema }),
  create: (body: CreateMaterialListRequest) =>
    request({ method: 'POST', path: '/material-lists', body, schema: MaterialListSchema }),
  update: (listId: string, body: UpdateMaterialListRequest) =>
    request({
      method: 'PATCH',
      path: `/material-lists/${listId}`,
      body,
      schema: MaterialListSchema,
    }),
  remove: (listId: string) =>
    request({ method: 'DELETE', path: `/material-lists/${listId}`, schema: OkResponseSchema }),

  addItems: (listId: string, body: CreateMaterialItemsRequest) =>
    request({
      method: 'POST',
      path: `/material-lists/${listId}/items`,
      body,
      schema: z.array(MaterialListItemSchema),
    }),
  updateItem: (listId: string, itemId: string, body: UpdateMaterialItemRequest) =>
    request({
      method: 'PATCH',
      path: `/material-lists/${listId}/items/${itemId}`,
      body,
      schema: MaterialListItemSchema,
    }),
  removeItem: (listId: string, itemId: string) =>
    request({
      method: 'DELETE',
      path: `/material-lists/${listId}/items/${itemId}`,
      schema: OkResponseSchema,
    }),
  recordQuote: (listId: string, itemId: string, body: RecordMaterialQuoteRequest) =>
    request({
      method: 'POST',
      path: `/material-lists/${listId}/items/${itemId}/quote`,
      body,
      schema: MaterialQuoteSchema,
    }),

  /** Returns the FULL detail so the caller gets fresh items + comparison at once. */
  price: (listId: string, body: PriceMaterialListRequest) =>
    request({
      method: 'POST',
      path: `/material-lists/${listId}/price`,
      body,
      schema: MaterialListDetailSchema,
    }),
  /** Import into a DRAFT estimate as material lines, at one vendor's prices. */
  importToEstimate: (listId: string, body: ImportMaterialListRequest) =>
    request({
      method: 'POST',
      path: `/material-lists/${listId}/import-to-estimate`,
      body,
      schema: z.object({
        estimateId: z.string().uuid(),
        sectionId: z.string().uuid(),
        linesCreated: z.number().int().min(0),
      }),
    }),
  comparison: (listId: string) =>
    request({
      path: `/material-lists/${listId}/comparison`,
      schema: MaterialComparisonSchema.nullable(),
    }),
  vendors: () => request({ path: '/material-vendors', schema: z.array(MaterialVendorSchema) }),
};

/**
 * Materials, subcontractor side. Addressed by LIST id only — there is
 * deliberately no assignment-id route, and no endpoint here falls back to a
 * contractor one. Every mutation returns the full re-priced detail.
 */
export const subMaterialsApi = {
  list: (params?: { projectId?: string }) =>
    request({
      path: '/sub/material-lists',
      query: q(params),
      schema: z.array(SubMaterialListSummarySchema),
    }),
  get: (listId: string) =>
    request({ path: `/sub/material-lists/${listId}`, schema: SubMaterialListDetailSchema }),
  addItems: (listId: string, body: CreateSubMaterialItemsRequest) =>
    request({
      method: 'POST',
      path: `/sub/material-lists/${listId}/items`,
      body,
      schema: SubMaterialListDetailSchema,
    }),
  updateItem: (listId: string, itemId: string, body: UpdateSubMaterialItemRequest) =>
    request({
      method: 'PATCH',
      path: `/sub/material-lists/${listId}/items/${itemId}`,
      body,
      schema: SubMaterialListDetailSchema,
    }),
};

/**
 * Tony — the in-product assistant. Placeholder: no model is wired, so a reply
 * comes back with `modelConnected: false` and the UI must say so rather than
 * render `answer` as though a model produced it.
 */
export const tonyApi = {
  ask: (body: AskTonyRequest) =>
    request({ method: 'POST', path: '/tony/ask', body, schema: TonyReplySchema }),
  suggestions: () =>
    request({ path: '/tony/suggestions', schema: TonySuggestionsResponseSchema }),
};

/** The Help centre. Same corpus the RAG index is built from. */
export const helpApi = {
  index: () => request({ path: '/help', schema: HelpIndexResponseSchema }),
  article: (slug: string) =>
    request({ path: `/help/${encodeURIComponent(slug)}`, schema: HelpArticleSchema }),
};
